import type { AttachmentAdapter } from "@assistant-ui/react";
import { toast } from "sonner";
import { aiAgentClient } from "../api/ai-agent-client";

/**
 * Cache of attachment adapters by salonId to ensure stable references
 */
const adapterCache = new Map<string, AttachmentAdapter>();

/**
 * Cache of uploaded attachment objects by ID to ensure stable references
 * This prevents infinite loops in useSyncExternalStore
 */
const attachmentObjectCache = new Map<string, any>();

/**
 * Get or create attachment adapter for a salon
 * Returns the same adapter instance for the same salonId to ensure stable reference
 */
export const getAttachmentAdapter = (salonId: string): AttachmentAdapter => {
  // Return cached adapter if exists
  const cachedAdapter = adapterCache.get(salonId);
  if (cachedAdapter) {
    return cachedAdapter;
  }

  // Create new adapter
  const adapter: AttachmentAdapter = {
    accept: "image/png,image/jpeg,image/jpg,application/pdf",

    async add({ file }) {
      try {
        // Validate file
        const isImage = file.type.startsWith("image/");
        const isPdf = file.type === "application/pdf";

        if (!isImage && !isPdf) {
          const error = "Only images (PNG, JPG) and PDF files are supported";
          toast.error(error);
          throw new Error(error);
        }

        // Check file size
        const maxSizeImage = 10 * 1024 * 1024; // 10MB
        const maxSizePdf = 25 * 1024 * 1024; // 25MB
        const maxSize = isImage ? maxSizeImage : maxSizePdf;

        if (file.size > maxSize) {
          const maxSizeMB = maxSize / (1024 * 1024);
          const error = `File size exceeds ${maxSizeMB}MB limit for ${isImage ? "images" : "PDFs"}`;
          toast.error(error);
          throw new Error(error);
        }

        // Upload to backend (just creates Asset record, not linked to thread yet)
        const uploadedAsset = await aiAgentClient.uploadAttachment(salonId, file);

        // Check if we already have this attachment cached
        const cachedAttachment = attachmentObjectCache.get(uploadedAsset.id);
        if (cachedAttachment) {
          return cachedAttachment;
        }

        // Create and cache attachment object to ensure stable reference
        const attachmentObj = {
          id: uploadedAsset.id,
          type: isImage ? ("image" as const) : ("document" as const),
          name: uploadedAsset.fileName,
          contentType: file.type,
          file,
          status: { type: "complete" } as const,
        };

        attachmentObjectCache.set(uploadedAsset.id, attachmentObj);
        return attachmentObj;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to upload attachment";
        toast.error(errorMessage);
        throw error;
      }
    },

    async remove(attachment) {
      // Delete the uploaded asset (before it's linked to any message)
      await aiAgentClient.deleteAsset(salonId, attachment.id);
      // Remove from cache
      attachmentObjectCache.delete(attachment.id);
    },

    async send(attachment) {
      // The file is already uploaded in `add`, so just finalize the attachment
      return {
        ...attachment,
        status: { type: "complete" as const },
        content: [],
      };
    },
  };

  // Cache the adapter
  adapterCache.set(salonId, adapter);
  return adapter;
};
